/**
 * Game Board Types
 * Type definitions for the game board plugin
 */

/**
 * Board space type
 */
export type BoardSpaceType = 'start' | 'prize' | 'roll-again' | 'grand-prize';

/**
 * Individual board space
 */
export interface BoardSpace {
  type: BoardSpaceType;
  position: number; // 0-23 (0 = START)
  prizeId?: string;
  prizeIndex?: number;
  prizeName?: string;
  prizeValue?: string;
  prizeIcon?: string;
  color?: string;
  side: 1 | 2 | 3 | 4; // Which side of the board (1 = right, 2 = top, 3 = left, 4 = bottom)
}

/**
 * Board orientation mode
 */
export type BoardOrientationMode = 'flat' | 'vertical';

/**
 * Board size presets (matches wheel sizes)
 */
export type BoardSize = 'small' | 'medium' | 'large';

/**
 * Physics constants for dice roller configuration
 */
export const GAME_BOARD_PHYSICS = {
  /** Base gravity for medium board size (scales proportionally with board for consistent fall times) */
  BASE_GRAVITY: 50,
  
  /** Die start height multiplier (units above floor surface) */
  DIE_START_HEIGHT_MULTIPLIER: 2,
  
  /** Pawn base scale for GLB model */
  PAWN_BASE_SCALE: 0.75,
} as const;

/**
 * Game board settings (matches Campaign.experience.settings for type='game-board')
 */
export interface GameBoardSettings {
  type: 'game-board';
  
  /**
   * Board size preset
   * - 'small': 750px (75% scale)
   * - 'medium': 1000px (baseline, 100% scale)
   * - 'large': 1250px (125% scale)
   */
  boardSize?: BoardSize;
  
  /**
   * Board orientation mode
   * - 'flat': Board lies horizontal (for flat markers/surfaces)
   * - 'vertical': Board perpendicular to marker (for vertical signs)
   */
  orientationMode?: BoardOrientationMode;
  
  /**
   * Extended play probability (0-1)
   * Chance that bottom-4 prizes trigger extended play
   * Default: 0.5 (50%)
   */
  extendedPlayProbability?: number;
  
  /**
   * Default prize colors (8 colors for 8+ prizes)
   */
  prizeColors?: string[];
  
  /**
   * Center logo URL (displayed in middle of board)
   */
  logoUrl?: string;
  
  /**
   * Center text (displayed if no logo)
   */
  centerText?: string;
  
  /**
   * Pawn color (hex or CSS color)
   * Default: '#4CAF50' (green)
   */
  pawnColor?: string;
  
  /**
   * Pawn scale multiplier (1.0 = default)
   * Affects size of game piece
   * Default: 1.0
   */
  pawnScale?: number;
  
  /**
   * Pawn rotation offset [x, y, z] in radians
   * Used to orient pawn correctly for board layout
   * Default: [0, -Math.PI/2, 0] (rotated 90° to face board direction)
   */
  pawnRotation?: [number, number, number];
  
  /**
   * Board color (hex or CSS color)
   * Default: '#654321' (rich brown)
   */
  boardColor?: string;
  
  /**
   * Board roughness (0-1)
   * Controls surface finish: 0 = glossy, 1 = matte
   * Default: 0.9 (matte wood)
   */
  boardRoughness?: number;
  
  /**
   * Board metalness (0-1)
   * Controls metallic appearance: 0 = non-metal, 1 = pure metal
   * Default: 0.1 (wood has minimal metallic properties)
   */
  boardMetalness?: number;
  
  /**
   * Board size multiplier (1.0 = default)
   */
  boardScale?: number;
  
  /**
   * Board depth/thickness (3D units)
   * Default: 0.2
   */
  boxDepth?: number;
  
  /**
   * Enable 3D shadows
   */
  castShadow?: boolean;
  
  /**
   * Enable orbit controls (for debugging/testing)
   */
  enableCameraControls?: boolean;
  
  // Preview mode flag (added by Portal)
  _previewPrizes?: any[];
}

/**
 * Game state for tracking roll progress
 */
export interface GameBoardState {
  currentPosition: number; // 0-23
  rollCount: number;
  cornerCount: number; // Count of ROLL AGAIN corners landed on
  winningPrizeId: string;
  winningPrizeIndex: number; // Index in config.items array
  winningPosition: number;
  isExtendedPlay: boolean;
  isRolling: boolean;
  isMoving: boolean;
  isComplete: boolean;
  hasWon: boolean; // True when winning roll completes
}

/**
 * Roll outcome (determined by Prize API + game logic)
 */
export interface RollOutcome {
  diceValue: number; // 1-6
  targetPosition: number; // Where piece should land
  isWinningRoll: boolean;
}

/**
 * Default prize colors (Monopoly-inspired)
 */
export const DEFAULT_PRIZE_COLORS = [
  '#4CAF50', // Green (Prize 1 - lowest value)
  '#9C27B0', // Purple (Prize 2)
  '#FFEB3B', // Yellow (Prize 3)
  '#00BCD4', // Cyan (Prize 4)
  '#E91E63', // Magenta (Prize 5)
  '#FF9800', // Orange (Prize 6)
  '#F44336', // Red (Prize 7 - 2nd highest)
  '#FFD700', // Gold (Prize 8 - grand prize)
];

/**
 * Unit-Based Sizing System
 * Eliminates fractional scaling, ensures pixel-perfect positioning
 */

// Logical unit dimensions
export const CORNER_UNITS = 4;        // Corner squares are 4×4 units
export const SPACE_WIDTH_UNITS = 3;   // Rectangular spaces are 3 units wide
export const SPACE_HEIGHT_UNITS = 4;  // Rectangular spaces are 4 units tall
export const SPACES_PER_SIDE = 5;     // Number of spaces between corners

// Calculate total board size in units
// Each side: 2 corners (4 units each) + 5 spaces (3 units wide each)
export const TOTAL_UNITS_PER_SIDE = (CORNER_UNITS * 2) + (SPACE_WIDTH_UNITS * SPACES_PER_SIDE); // 23 units

// Pixels per unit for each size preset
export const PIXELS_PER_UNIT: Record<BoardSize, number> = {
  small: 32.61,   // 23 × 32.61 ≈ 750px board
  medium: 43.48,  // 23 × 43.48 ≈ 1000px board
  large: 54.35    // 23 × 54.35 ≈ 1250px board
};

// Board pixel sizes (clean values for easy scaling)
export const BOARD_SIZE_PIXELS: Record<BoardSize, number> = {
  small: 750,   // 75% of medium
  medium: 1000, // Baseline
  large: 1250   // 125% of medium
};

